// V196 — Build fee reminder text from ONLY the details selected for this reminder.
const clean = (v) => {
    const s = String(v ?? "").trim();
    return /^(null|undefined|none|nan)$/i.test(s) ? "" : s;
};
export const listJoin = (xs) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

export const emptyRow = () => ({ tutor: "", grades: [], subjects: [], sessions: "" });

/** Normalised rows with at least one value; blanks/"null"/"undefined" never survive. */
export function usableRows(rows = []) {
    return rows
        .map((r) => ({
            tutor: clean(r.tutor),
            grades: (r.grades || []).map(clean).filter(Boolean),
            subjects: (r.subjects || []).map(clean).filter(Boolean),
            sessions: Number.parseInt(r.sessions, 10) > 0 ? Number.parseInt(r.sessions, 10) : null,
        }))
        .filter((r) => r.tutor || r.grades.length || r.subjects.length || r.sessions);
}

// "16 Biology sessions" / "Biology sessions" / "1 session"
let MODE_WORD = "";
function nounPhrase(r) {
    const subj = r.subjects.length ? `${listJoin(r.subjects)} ` : "";
    const n = r.sessions ? `${r.sessions} ` : "";
    return `${n}${subj}${MODE_WORD}session${r.sessions === 1 ? "" : "s"}`;
}
const forGrades = (r) => (r.grades.length ? ` for ${listJoin(r.grades)}` : "");

/** Bullet text for one combination, e.g. "16 Biology sessions for Class 11 with Ishika Ma'am". */
export function rowItem(r) {
    return `${cap(nounPhrase(r))}${forGrades(r)}${r.tutor ? ` with ${r.tutor}` : ""}`;
}

/** "Sessions Completed" paragraph for one combination. */
function completedSentence(r) {
    const covered = r.sessions ? "" : " covered by your previous payment";
    const obj = `${r.sessions ? "" : "the "}${nounPhrase(r)}${forGrades(r)}${covered}`;
    if (r.tutor) return `${r.tutor} has completed ${obj}.`;
    const verb = r.sessions === 1 ? "has" : "have";
    return `${cap(obj)} ${verb} been completed.`;
}

const PAY_NEXT = "Kindly arrange the payment for the upcoming sessions so that classes can continue without interruption.";
const PAY_NEXT_HOME = "Kindly arrange the payment for the next cycle so that the tutor can continue the sessions without interruption.";

const SYMBOL = { INR: "₹", USD: "$", GBP: "£", EUR: "€" };
export const CURRENCIES = ["INR", "USD", "AED", "GBP", "EUR", "SGD", "CAD", "AUD", "QAR", "SAR"];

/** "₹5,000" / "AED 500" — never converts, only formats what was typed. */
export function money(value, currency = "INR") {
    const raw = clean(value).replace(/,/g, "");
    if (!raw) return "";
    const num = Number(raw);
    const dec = /\.\d/.test(raw) ? 2 : 0;
    const shown = Number.isFinite(num)
        ? num.toLocaleString(currency === "INR" ? "en-IN" : "en-US", { minimumFractionDigits: dec, maximumFractionDigits: 2 })
        : clean(value);
    return SYMBOL[currency] ? `${SYMBOL[currency]}${shown}` : `${currency} ${shown}`;
}

export function niceDate(iso) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(clean(iso))) return "";
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

const STATUS_LINE = {
    active: "The classes are currently active.",
    hold: "The classes are currently on hold.",
    awaiting: "We are awaiting your confirmation to continue the classes.",
};

/** Sentences for the optional extra fields — only those actually filled in. */
export function infoSentences(f = {}) {
    const out = [];
    const amt = money(f.amount, f.currency || "INR");
    const pend = money(f.pending, f.pending_currency || f.currency || "INR");
    if (amt) out.push(`The fee amount is ${amt}.`);
    if (pend) out.push(`The pending amount is ${pend}.`);
    if (niceDate(f.due_date)) out.push(`The payment is due on ${niceDate(f.due_date)}.`);
    if (niceDate(f.promised_date)) out.push(`As per our earlier conversation, the payment was expected by ${niceDate(f.promised_date)}.`);
    if (niceDate(f.prev_date)) out.push(`This is a follow-up to our reminder sent on ${niceDate(f.prev_date)}.`);
    if (niceDate(f.next_cycle)) out.push(`The next cycle is scheduled to begin on ${niceDate(f.next_cycle)}.`);
    if (STATUS_LINE[f.status]) out.push(STATUS_LINE[f.status]);
    return out.join(" ");
}

function amountLine(fields) {
    return typeof fields === "object" ? infoSentences(fields) : (clean(fields) ? `The fee amount is ${clean(fields)}.` : "");
}

/** Paragraph describing the selected details, or "" when nothing is selected. */
export function detailsParagraph(rows, amount, mode) {
    const rs = usableRows(rows);
    const amt = amountLine(amount);
    if (mode === "replace") {
        if (!rs.length) return amt ? null : ""; // null → keep template paragraph, append amount
        const head = rs.length === 1
            ? completedSentence(rs[0])
            : `The following sessions have been completed:\n${rs.map((r) => `• ${rowItem(r)}`).join("\n")}\n`;
        return [head.trimEnd(), MODE_WORD === "home tuition " ? PAY_NEXT_HOME : PAY_NEXT, amt].filter(Boolean).join(rs.length === 1 ? " " : "\n\n");
    }
    if (!rs.length) return amt;
    const lead = rs.length === 1
        ? `This reminder is regarding ${rs[0].sessions ? "" : "the "}${nounPhrase(rs[0])}${forGrades(rs[0])}${rs[0].tutor ? ` with ${rs[0].tutor}` : ""}.`
        : `This reminder is regarding:\n${rs.map((r) => `• ${rowItem(r)}`).join("\n")}`;
    return [lead, amt].filter(Boolean).join(rs.length === 1 ? " " : "\n\n");
}

const GREET_RE = /^Greetings from Anand Tutorials!\s*$/i;
const DEAR_RE = /^Dear [^\n]*,$/;

/**
 * Apply greeting + details + extra fields to a template body.
 * `fields` = { amount, currency, pending, due_date, promised_date, prev_date, next_cycle, status, notes }.
 * With nothing selected the body is returned unchanged.
 */
export function applyDetails(body = "", { rows = [], amount = "", fields = null, greeting = "", mode = "insert", teaching = "" } = {}) {
    MODE_WORD = teaching === "home" ? "home tuition " : teaching === "online" ? "online " : "";
    let paras = body.split(/\n{2,}/);
    if (greeting) paras = paras.map((p) => (DEAR_RE.test(p.trim()) ? `Dear ${greeting},` : p));
    const para = detailsParagraph(rows, fields || amount, mode);
    const notes = clean(fields?.notes);
    const gi = paras.findIndex((p) => GREET_RE.test(p.trim()));
    const di = paras.findIndex((p) => DEAR_RE.test(p.trim()));
    const at = gi >= 0 ? gi + 1 : di >= 0 ? di + 1 : Math.min(1, paras.length);
    if (notes) {
        const end = paras.findIndex((p) => /^(Best regards|Regards),/i.test(p.trim()));
        paras.splice(end >= 0 ? end : paras.length, 0, notes);
    }
    if (para === "") return paras.join("\n\n");
    if (mode === "replace") {
        if (para === null) paras.splice(at + 1, 0, amountLine(fields || amount));
        else paras.splice(at, 1, para);
    } else {
        paras.splice(at, 0, para);
    }
    return paras.join("\n\n");
}

export const isReplaceTemplate = (t) => t?.details_mode === "replace" || /sessions completed/i.test(t?.name || "");
